import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, MapPin, Clock, Users, ChatCircle, 
  Trash, Play, Trophy, CheckCircle, GameController, Camera, Handshake,
  Backpack, Plus, MagnifyingGlass, X, Star, Check, CaretDown,
  Lock, EnvelopeSimple, UserCheck, Globe, PencilSimple, ShareNetwork, Link as LinkIcon, Copy, PaperPlaneTilt,
  Flag
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import GamePill from '../components/GamePill';
import { RulePackViewer } from './shop/ShopRulePackViews';
import { toast } from 'sonner';
import { useLogoFlash } from '../components/LogoFlash';
import ReportModal from '../components/ReportModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedBy = searchParams.get('invited_by') || null;
  const { user } = useAuth();
  const { flash, FlashRenderer } = useLogoFlash();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRecordMatch, setShowRecordMatch] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState('');
  const [planningData, setPlanningData] = useState(null);
  const [showProposeGame, setShowProposeGame] = useState(false);
  const [gameSearch, setGameSearch] = useState('');
  const [gameResults, setGameResults] = useState([]);
  const [showPlanning, setShowPlanning] = useState(false);

  // Join request / privacy state
  const [showJoinRequest, setShowJoinRequest] = useState(false);
  const [joinRequestMessage, setJoinRequestMessage] = useState('');
  const [myRequestStatus, setMyRequestStatus] = useState(null); // pending | approved | rejected | null
  const [pendingRequests, setPendingRequests] = useState([]);
  const [decidedRequests, setDecidedRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);

  // Share / Invite state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTab, setShareTab] = useState('invite'); // invite | link
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [friendSearch, setFriendSearch] = useState('');
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Compute derived state early to avoid reference errors
  const isHost = session?.host_id === user?.id;
  const isParticipant = session?.participants?.some(p => p.id === user?.id);
  const isFull = session?.current_players >= session?.max_players;
  const isCompleted = session?.status === 'completed';
  const isInProgress = session?.status === 'in_progress';

  useEffect(() => {
    fetchSession();
  }, [id]);

  useEffect(() => {
    if (session && isParticipant) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [session?.id, isParticipant]);

  const fetchSession = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/${id}`, { withCredentials: true });
      setSession(data);
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error('Session not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/${id}/messages`, { withCredentials: true });
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      // User might not be participant yet
    }
  };

  const handleJoin = async () => {
    // If session requires approval, route to request-to-join modal instead.
    if (session?.requires_approval && !session?.shop_id) {
      setShowJoinRequest(true);
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/join`, {}, { withCredentials: true });
      flash({ message: "You're in!", tone: 'success' });
      toast.success('Joined session!');
      fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    } finally {
      setActionLoading(false);
    }
  };

  const submitJoinRequest = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/sessions/${id}/join-requests`,
        { message: joinRequestMessage.trim(), invited_by: invitedBy },
        { withCredentials: true }
      );
      flash({ message: 'Request sent!', tone: 'success' });
      toast.success('Your join request was sent to the host');
      setShowJoinRequest(false);
      setJoinRequestMessage('');
      setMyRequestStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchMyRequestStatus = useCallback(async () => {
    if (!user || !session || isHost || isParticipant) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/my-join-requests`, { withCredentials: true });
      const match = data.find(r => r.session_id === id);
      setMyRequestStatus(match?.status || null);
    } catch (e) {
      // silent — outgoing requests are optional info
    }
  }, [id, user, session, isHost, isParticipant]);

  const fetchHostRequests = useCallback(async () => {
    if (!isHost) return;
    setRequestsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/${id}/join-requests`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      setPendingRequests(list.filter(r => r.status === 'pending'));
      setDecidedRequests(list.filter(r => r.status !== 'pending'));
    } catch (e) {
      // silent
    } finally {
      setRequestsLoading(false);
    }
  }, [id, isHost]);

  const decideRequest = async (requestId, approve) => {
    try {
      const url = `${API_URL}/api/sessions/${id}/join-requests/${requestId}/${approve ? 'approve' : 'reject'}`;
      await axios.post(url, {}, { withCredentials: true });
      toast.success(approve ? 'Player approved' : 'Request rejected');
      fetchHostRequests();
      if (approve) fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    }
  };

  const openShareModal = async (tab = 'invite') => {
    setShareTab(tab);
    setShowShareModal(true);
    setSelectedFriendIds([]);
    setFriendSearch('');
    if (tab === 'invite' && friends.length === 0) {
      setFriendsLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/friends`, { withCredentials: true });
        const list = Array.isArray(data) ? data : [];
        const participantIds = new Set(session?.participants?.map(p => p.id) || []);
        // Hide friends already in the session
        setFriends(list.filter(f => !participantIds.has(f.id)));
      } catch (e) {
        // silent
      } finally {
        setFriendsLoading(false);
      }
    }
  };

  const toggleFriendSelection = (fid) => {
    setSelectedFriendIds(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  };

  const sendInvites = async () => {
    if (selectedFriendIds.length === 0) {
      toast.error('Pick at least one friend');
      return;
    }
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sessions/${id}/invites`,
        { user_ids: selectedFriendIds },
        { withCredentials: true }
      );
      toast.success(`Invited ${data.sent} friend${data.sent === 1 ? '' : 's'}`);
      setShowShareModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invites');
    } finally {
      setInviting(false);
    }
  };

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/events/${id}${user?.id ? `?invited_by=${user.id}` : ''}`
    : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy — long-press the link to copy');
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyShareLink();
      return;
    }
    try {
      await navigator.share({
        title: session?.title || 'Join my game',
        text: `Join my ${session?.game_type || 'game'} session on CyBoard — ${session?.date} ${session?.time}`,
        url: shareUrl,
      });
    } catch {
      // user cancelled — silent
    }
  };

  const filteredFriends = friends.filter(f =>
    !friendSearch || (f.name || '').toLowerCase().includes(friendSearch.toLowerCase()) ||
    (f.username || '').toLowerCase().includes(friendSearch.toLowerCase())
  );

  useEffect(() => {
    if (session && !isHost && !isParticipant && session.requires_approval && !session.shop_id) {
      fetchMyRequestStatus();
    }
  }, [session?.id, isHost, isParticipant, fetchMyRequestStatus]);

  useEffect(() => {
    if (session && isHost) {
      fetchHostRequests();
    }
  }, [session?.id, isHost, fetchHostRequests]);

  const handleLeave = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/leave`, {}, { withCredentials: true });
      toast.success('Left session');
      fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await axios.delete(`${API_URL}/api/sessions/${id}`, { withCredentials: true });
      toast.success('Session deleted');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleStart = async () => {
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/start`, {}, { withCredentials: true });
      toast.success('Game started!');
      fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    }
  };

  const handleRecordMatch = async () => {
    if (!selectedWinner) {
      toast.error('Please select a winner');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/matches`, {
        session_id: id,
        game_type: session.game_type,
        winner_id: selectedWinner,
        player_ids: session.participants.map(p => p.id)
      }, { withCredentials: true });
      toast.success('Match recorded!');
      setShowRecordMatch(false);
      fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record match');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/messages`, {
        content: newMessage.trim()
      }, { withCredentials: true });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const fetchPlanning = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/api/sessions/${id}/what-can-we-play`, { withCredentials: true });
      setPlanningData(res.data);
    } catch (e) { /* silent */ }
  }, [id]);

  useEffect(() => {
    if (isParticipant && session) fetchPlanning();
  }, [isParticipant, session?.id, fetchPlanning]);

  const searchGamesForPropose = useCallback(async (q) => {
    if (!q || q.length < 2) { setGameResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/games-browse?search=${encodeURIComponent(q)}&limit=8`);
      setGameResults(res.data);
    } catch (e) { setGameResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchGamesForPropose(gameSearch), 300);
    return () => clearTimeout(t);
  }, [gameSearch, searchGamesForPropose]);

  const proposeGame = async (gameId) => {
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/game-options`, {
        session_id: id, game_id: gameId, source_type: isHost ? 'host_suggested' : 'player_collection', status: 'proposed',
      }, { withCredentials: true });
      toast.success('Game proposed!');
      setShowProposeGame(false);
      setGameSearch('');
      fetchPlanning();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateGameOption = async (optionId, status) => {
    try {
      await axios.put(`${API_URL}/api/sessions/${id}/game-options/${optionId}`, { status }, { withCredentials: true });
      toast.success('Updated!');
      fetchPlanning();
    } catch (e) { toast.error('Failed'); }
  };

  const setCarrier = async (gameId, status) => {
    try {
      await axios.post(`${API_URL}/api/sessions/${id}/carriers`, {
        session_id: id, game_id: gameId, carrier_status: status,
      }, { withCredentials: true });
      toast.success(status === 'bringing' ? "You're bringing it!" : 'Updated');
      fetchPlanning();
    } catch (e) { toast.error('Failed'); }
  };

  const defaultImage = 'https://images.unsplash.com/photo-1547638375-ebf04735d792?w=600&h=300&fit=crop';

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-[#39FF14] bg-[#39FF14]/10';
      case 'full': return 'text-[#FACC15] bg-[#FACC15]/10';
      case 'in_progress': return 'text-[#00F0FF] bg-[#00F0FF]/10';
      case 'completed': return 'text-[#A1A1AA] bg-[#A1A1AA]/10';
      default: return 'text-[#71717A] bg-[#71717A]/10';
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="app-container" data-testid="session-details-page">
      <FlashRenderer />
      {/* Header Image */}
      <div className="relative h-48">
        <img src={session.image || defaultImage} alt={session.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06060A] via-transparent to-transparent"></div>
        
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full glass flex items-center justify-center"
          data-testid="back-button"
        >
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>

        <div className="absolute top-4 right-4 flex gap-2">
          {isHost && !isCompleted && (
            <>
              <button onClick={() => openShareModal('invite')} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="share-event-btn" title="Invite & share">
                <ShareNetwork size={20} weight="duotone" className="text-[#00F0FF]" />
              </button>
              <button onClick={() => navigate(`/events/${id}/edit`)} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="edit-session" title="Edit event">
                <PencilSimple size={20} weight="duotone" className="text-[#FACC15]" />
              </button>
              <button onClick={handleDelete} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="delete-session">
                <Trash size={20} weight="duotone" className="text-red-400" />
              </button>
            </>
          )}
          {!isHost && (
            <>
              <button onClick={() => openShareModal('link')} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="share-event-btn" title="Share link">
                <ShareNetwork size={20} weight="duotone" className="text-[#00F0FF]" />
              </button>
              <button onClick={() => setShowReportModal(true)} className="w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="report-event-btn" title="Report event">
                <Flag size={20} weight="duotone" className="text-[#FACC15]" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 -mt-6 relative z-10 pb-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-2">
            <GamePill game={session.game_type} />
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
              {session.status.replace('_', ' ')}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-white mt-3 mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {session.title}
          </h1>

          {/* Info Cards */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5">
              <div className="flex items-center gap-2 text-[#00F0FF] mb-1">
                <Clock size={16} weight="duotone" />
                <span className="text-xs font-medium">When</span>
              </div>
              <p className="text-white text-sm font-medium">{session.date}</p>
              <p className="text-[#A1A1AA] text-xs">{session.time}</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5" data-testid="session-location-card">
              <div className="flex items-center gap-2 text-[#9D4CDD] mb-1">
                <MapPin size={16} weight="duotone" />
                <span className="text-xs font-medium">Where</span>
                {session.requires_approval && !session.location_visible && (
                  <Lock size={12} weight="fill" className="text-[#9D4CDD] ml-auto" />
                )}
                {!session.requires_approval && (
                  <Globe size={12} weight="duotone" className="text-[#39FF14] ml-auto" />
                )}
              </div>
              {session.location_visible && session.location ? (
                <>
                  <p className="text-white text-sm font-medium line-clamp-2" data-testid="session-full-address">{session.location}</p>
                  {(session.location_city || session.location_zip) && (
                    <p className="text-[#71717A] text-[10px] mt-0.5">
                      {[session.location_city, session.location_zip].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-white text-sm font-medium line-clamp-2" data-testid="session-area-only">
                    {[session.location_city, session.location_zip].filter(Boolean).join(' · ') || 'Area hidden by host'}
                  </p>
                  <p className="text-[10px] text-[#9D4CDD]/80 mt-1 flex items-center gap-1">
                    <Lock size={9} weight="fill" /> Exact address shared after host approval
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mt-5">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-2">About</h3>
            <p className="text-white text-sm leading-relaxed">{session.description}</p>
          </div>

          {/* Rule Packs for this session */}
          <div className="mt-5">
            <RulePackViewer targetType="session" targetId={id} />
          </div>

          {/* Host */}
          <div className="mt-5 p-4 rounded-2xl bg-[#0F0F16] border border-white/5">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Host</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {session.host_avatar ? (
                  <img src={session.host_avatar} alt={session.host_name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-sm font-bold text-white">
                    {session.host_name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{session.host_name}</p>
                  {session.host_username && <p className="text-[#71717A] text-xs">@{session.host_username}</p>}
                </div>
              </div>
              {!isHost && (
                <button
                  onClick={() => navigate(`/chat/${session.host_id}`)}
                  className="btn-secondary py-2 px-3 text-sm flex items-center gap-1"
                  data-testid="message-host"
                >
                  <ChatCircle size={16} weight="duotone" />
                  Message
                </button>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#A1A1AA]">
                Players ({session.current_players}/{session.max_players})
              </h3>
              <Users size={18} weight="duotone" className="text-[#39FF14]" />
            </div>
            <div className="flex flex-wrap gap-2">
              {session.participants?.map((p) => (
                <div key={p.id} className="flex items-center gap-2 p-2 rounded-xl bg-[#0F0F16] border border-white/5">
                  {p.avatar ? (
                    <img src={p.avatar} alt={p.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-xs font-bold text-white">
                      {p.name?.charAt(0)}
                    </div>
                  )}
                  <span className="text-sm text-white">{p.name}</span>
                  {p.id === session.host_id && <span className="text-[10px] text-[#FACC15]">HOST</span>}
                </div>
              ))}
            </div>
          </div>

          {/* ============ HOST JOIN-REQUEST APPROVAL PANEL ============ */}
          {isHost && !isCompleted && (pendingRequests.length > 0 || decidedRequests.length > 0) && (
            <div className="mt-5" data-testid="host-requests-panel">
              <button
                onClick={() => setShowRequestsPanel(v => !v)}
                className="w-full p-3 rounded-2xl bg-[#0F0F16] border border-[#9D4CDD]/20 hover:border-[#9D4CDD]/40 transition-all flex items-center justify-between"
                data-testid="toggle-requests-panel"
              >
                <div className="flex items-center gap-2">
                  <UserCheck size={18} weight="duotone" className="text-[#9D4CDD]" />
                  <span className="text-sm font-semibold text-white">Join Requests</span>
                  {pendingRequests.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-[#9D4CDD]/20 text-[#9D4CDD] text-[10px] font-bold" data-testid="pending-requests-count">
                      {pendingRequests.length} pending
                    </span>
                  )}
                </div>
                <CaretDown size={16} className={`text-[#71717A] transition-transform ${showRequestsPanel ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showRequestsPanel && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mt-2 space-y-2"
                  >
                    {requestsLoading && (
                      <div className="text-center py-4">
                        <div className="inline-block w-5 h-5 border-2 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}

                    {!requestsLoading && pendingRequests.length === 0 && decidedRequests.length === 0 && (
                      <p className="text-center text-[#71717A] text-xs py-3">No requests yet</p>
                    )}

                    {pendingRequests.map(r => (
                      <div key={r.id} className="p-3 rounded-xl bg-[#0F0F16] border border-[#9D4CDD]/15" data-testid={`request-${r.id}`}>
                        <div className="flex items-start gap-3">
                          {r.requester_avatar ? (
                            <img src={r.requester_avatar} alt={r.requester_name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-sm font-bold text-white shrink-0">
                              {r.requester_name?.charAt(0)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => navigate(`/profile/${r.requester_id}`)}
                                className="text-white text-sm font-medium hover:underline truncate"
                              >
                                {r.requester_name}
                              </button>
                              {r.requester_username && <span className="text-[#71717A] text-[10px]">@{r.requester_username}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-[10px] text-[#A1A1AA]">Lv {r.requester_level || 0}</span>
                              <span className="text-[10px] text-[#71717A]">·</span>
                              <span className="text-[10px] text-[#A1A1AA]">{r.requester_total_games || 0} games</span>
                              <span className="text-[10px] text-[#71717A]">·</span>
                              <span className="text-[10px] text-[#39FF14]">{r.requester_wins || 0} wins</span>
                              {r.requester_location && (
                                <>
                                  <span className="text-[10px] text-[#71717A]">·</span>
                                  <span className="text-[10px] text-[#A1A1AA] flex items-center gap-0.5">
                                    <MapPin size={9} /> {r.requester_location}
                                  </span>
                                </>
                              )}
                            </div>
                            {r.message && (
                              <p className="text-[#E4E4E7] text-xs mt-2 p-2 rounded-lg bg-[#0a0a12] italic">
                                "{r.message}"
                              </p>
                            )}
                            {r.invited_by_name && (
                              <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/20" data-testid={`invited-by-${r.id}`}>
                                <ShareNetwork size={9} weight="duotone" className="text-[#00F0FF]" />
                                <span className="text-[9px] text-[#00F0FF] font-medium">Invited by {r.invited_by_name}</span>
                              </div>
                            )}
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => decideRequest(r.id, true)}
                                className="flex-1 py-1.5 px-3 rounded-lg bg-[#39FF14]/10 text-[#39FF14] hover:bg-[#39FF14]/20 text-xs font-semibold flex items-center justify-center gap-1 transition-all"
                                data-testid={`approve-request-${r.id}`}
                              >
                                <Check size={14} weight="bold" /> Approve
                              </button>
                              <button
                                onClick={() => decideRequest(r.id, false)}
                                className="flex-1 py-1.5 px-3 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-semibold flex items-center justify-center gap-1 transition-all"
                                data-testid={`reject-request-${r.id}`}
                              >
                                <X size={14} weight="bold" /> Decline
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {decidedRequests.length > 0 && (
                      <div className="pt-2 border-t border-white/[0.04]">
                        <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1.5 px-1">Recent Decisions</p>
                        <div className="space-y-1">
                          {decidedRequests.slice(0, 5).map(r => (
                            <div key={r.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#0a0a12]">
                              <span className="text-white text-xs flex-1 truncate">{r.requester_name}</span>
                              <span className={`text-[10px] font-medium ${r.status === 'approved' ? 'text-[#39FF14]' : 'text-red-400'}`}>
                                {r.status === 'approved' ? 'Approved' : 'Declined'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Session Chat (if participant) */}
          {isParticipant && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Game Tools</h3>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => navigate(`/live-tracker/${id}`)} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-[#00F0FF]/30 transition-all text-center" data-testid="live-tracker-link">
                  <GameController size={20} weight="duotone" className="text-[#00F0FF] mx-auto mb-1" />
                  <p className="text-white text-[10px] font-medium">Live Tracker</p>
                </button>
                <button onClick={() => navigate(`/ai?game=${session.game_type}`)} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-[#9D4CDD]/30 transition-all text-center" data-testid="session-ai-link">
                  <Camera size={20} weight="duotone" className="text-[#9D4CDD] mx-auto mb-1" />
                  <p className="text-white text-[10px] font-medium">AI / Snapshots</p>
                </button>
                <button onClick={() => navigate(`/rules?game=${session.game_type}`)} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-[#FACC15]/30 transition-all text-center" data-testid="session-rules-link">
                  <Trophy size={20} weight="duotone" className="text-[#FACC15] mx-auto mb-1" />
                  <p className="text-white text-[10px] font-medium">Rules</p>
                </button>
              </div>
            </div>
          )}

          {/* What Can We Play? Section */}
          {isParticipant && !isCompleted && (
            <div className="mt-5" data-testid="what-can-we-play-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-1.5">
                  <GameController size={14} weight="duotone" className="text-[#10B981]" />
                  What Can We Play?
                </h3>
                <div className="flex gap-1.5">
                  <button onClick={() => setShowPlanning(!showPlanning)}
                    className="text-[10px] px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20"
                    data-testid="toggle-planning"
                  >
                    {showPlanning ? 'Collapse' : 'Expand'}
                  </button>
                  <button onClick={() => setShowProposeGame(true)}
                    className="text-[10px] px-2.5 py-1 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD] hover:bg-[#9D4CDD]/20 flex items-center gap-1"
                    data-testid="propose-game-btn"
                  >
                    <Plus size={10} weight="bold" /> Propose Game
                  </button>
                </div>
              </div>

              {/* Proposed Games */}
              {planningData?.proposed_games?.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {planningData.proposed_games.map(g => {
                    const statusColors = { proposed: '#F59E0B', selected: '#10B981', backup_option: '#3B82F6', rejected: '#6B7280' };
                    const myCarrier = planningData.carriers?.find(c => c.game_id === g.game_id && c.user_id === user?.id);
                    return (
                      <div key={g.id} className="p-2.5 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`proposed-game-${g.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${statusColors[g.status]}15` }}>
                            <GameController size={16} weight="duotone" style={{ color: statusColors[g.status] }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{g.game_name || 'Unknown'}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${statusColors[g.status]}20`, color: statusColors[g.status] }}>
                                {g.status}
                              </span>
                              <span className="text-[9px] text-[#555]">by {g.proposed_by_user_id_name || 'Unknown'}</span>
                              {g.carrier_count > 0 && <span className="text-[9px] text-[#10B981]"><Backpack size={9} className="inline" /> {g.carrier_count}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {/* Carrier toggle */}
                            <button
                              onClick={() => setCarrier(g.game_id, myCarrier?.carrier_status === 'bringing' ? 'not_bringing' : 'bringing')}
                              className={`p-1.5 rounded-lg text-[10px] ${myCarrier?.carrier_status === 'bringing' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/[0.04] text-[#666]'}`}
                              title={myCarrier?.carrier_status === 'bringing' ? "You're bringing this" : "I can bring this"}
                              data-testid={`carrier-toggle-${g.game_id}`}
                            >
                              <Backpack size={14} />
                            </button>
                            {/* Host controls */}
                            {isHost && g.status === 'proposed' && (
                              <>
                                <button onClick={() => updateGameOption(g.id, 'selected')}
                                  className="p-1.5 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20"
                                  data-testid={`select-game-${g.id}`}
                                ><Check size={14} /></button>
                                <button onClick={() => updateGameOption(g.id, 'rejected')}
                                  className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                  data-testid={`reject-game-${g.id}`}
                                ><X size={14} /></button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Expanded planning view */}
              <AnimatePresence>
                {showPlanning && planningData && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    {/* Carriers */}
                    {planningData.carriers?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1.5">Who's Bringing What</p>
                        <div className="space-y-1">
                          {planningData.carriers.filter(c => c.carrier_status === 'bringing' || c.carrier_status === 'maybe_bringing').map(c => (
                            <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#0a0a12]">
                              <Backpack size={12} className="text-[#10B981]" />
                              <span className="text-white text-xs">{c.user_name}</span>
                              <span className="text-[#555] text-[9px]">bringing</span>
                              <span className="text-[#10B981] text-xs font-medium">{c.game_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Attendee collections */}
                    {planningData.attendee_collections?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1.5">Games Attendees Own</p>
                        <div className="flex flex-wrap gap-1.5">
                          {planningData.attendee_collections.map(g => (
                            <button key={g.game_id} onClick={() => proposeGame(g.game_id)}
                              className="px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-medium hover:bg-[#F59E0B]/20 flex items-center gap-1"
                              title={`Owned by: ${g.owners.map(o => o.name).join(', ')}`}
                            >
                              <Plus size={8} /> {g.game_name} ({g.owners.length})
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Shop available */}
                    {planningData.shop_available_games?.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1.5">Available at Shop</p>
                        <div className="flex flex-wrap gap-1.5">
                          {planningData.shop_available_games.map(g => (
                            <span key={g.id} className="px-2 py-1 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] text-[10px] font-medium">
                              {g.game_name} · {g.availability_state.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Propose Game Modal */}
              <AnimatePresence>
                {showProposeGame && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
                    onClick={e => e.target === e.currentTarget && setShowProposeGame(false)}
                  >
                    <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
                      className="bg-[#0e0e16] border border-white/[0.06] rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] overflow-hidden"
                      data-testid="propose-game-modal"
                    >
                      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Propose a Game</h3>
                        <button onClick={() => setShowProposeGame(false)} className="text-[#666] hover:text-white"><X size={20} /></button>
                      </div>
                      <div className="p-4">
                        <div className="relative mb-3">
                          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                          <input value={gameSearch} onChange={e => setGameSearch(e.target.value)}
                            placeholder="Search games..." autoFocus
                            className="w-full bg-[#0a0a12] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40"
                            data-testid="propose-game-search"
                          />
                        </div>
                        <div className="max-h-[50vh] overflow-y-auto space-y-1">
                          {gameResults.map(g => (
                            <button key={g.id} onClick={() => proposeGame(g.id)}
                              className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] text-left"
                              data-testid={`propose-${g.slug}`}
                            >
                              <div className="w-8 h-8 rounded bg-[#9D4CDD]/10 flex items-center justify-center shrink-0">
                                <GameController size={16} className="text-[#9D4CDD]" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate">{g.name}</p>
                                <p className="text-[#555] text-[10px]">{g.publisher} · {g.min_players}-{g.max_players}p · {g.complexity}</p>
                              </div>
                            </button>
                          ))}
                          {gameSearch.length < 2 && <p className="text-center text-[#555] text-xs py-4">Type to search...</p>}
                          {gameSearch.length >= 2 && gameResults.length === 0 && <p className="text-center text-[#555] text-xs py-4">No games found</p>}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Session Chat (if participant) */}
          {isParticipant && !isCompleted && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Session Chat</h3>
              <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 max-h-48 overflow-y-auto mb-2">
                {messages.length === 0 ? (
                  <p className="text-[#71717A] text-sm text-center py-4">No messages yet</p>
                ) : (
                  <div className="space-y-2">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex gap-2 ${msg.sender_id === user?.id ? 'flex-row-reverse' : ''}`}>
                        <div className={`max-w-[75%] p-2 rounded-xl text-sm ${
                          msg.sender_id === user?.id 
                            ? 'bg-[#9D4CDD]/20 text-white' 
                            : 'bg-[#181824] text-[#A1A1AA]'
                        }`}>
                          <p className="text-[10px] font-medium text-[#71717A] mb-0.5">{msg.sender_name}</p>
                          <p>{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="input-field flex-1 py-2 text-sm"
                  data-testid="session-message-input"
                />
                <button type="submit" className="btn-primary py-2 px-4 text-sm" data-testid="send-session-message">
                  Send
                </button>
              </form>
            </div>
          )}

          {/* Record Match Modal */}
          {showRecordMatch && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-sm bg-[#0F0F16] rounded-3xl p-6 border border-white/10"
              >
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  <Trophy size={24} className="inline mr-2 text-[#FACC15]" />
                  Record Match Result
                </h3>
                <p className="text-[#A1A1AA] text-sm mb-4">Select the winner:</p>
                <div className="space-y-2 mb-6">
                  {session.participants?.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedWinner(p.id)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                        selectedWinner === p.id 
                          ? 'bg-[#39FF14]/20 border border-[#39FF14]/50' 
                          : 'bg-[#181824] border border-transparent hover:border-white/10'
                      }`}
                    >
                      {p.avatar ? (
                        <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-xs font-bold text-white">
                          {p.name?.charAt(0)}
                        </div>
                      )}
                      <span className="text-white">{p.name}</span>
                      {selectedWinner === p.id && <CheckCircle size={20} className="ml-auto text-[#39FF14]" weight="fill" />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowRecordMatch(false)} className="btn-secondary flex-1">Cancel</button>
                  <button onClick={handleRecordMatch} className="btn-primary flex-1">Record</button>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md p-6 bg-gradient-to-t from-[#06060A] to-transparent">
        {isCompleted ? (
          <button className="btn-secondary w-full" disabled>
            <CheckCircle size={20} className="inline mr-2" />
            Session Completed
          </button>
        ) : isHost ? (
          <div className="flex gap-2">
            {session.status === 'open' || session.status === 'full' ? (
              <button onClick={handleStart} className="btn-primary flex-1" data-testid="start-session">
                <Play size={20} className="inline mr-1" weight="fill" />
                Start Game
              </button>
            ) : isInProgress ? (
              <button onClick={() => setShowRecordMatch(true)} className="btn-primary flex-1" data-testid="record-match">
                <Trophy size={20} className="inline mr-1" weight="fill" />
                Record Result
              </button>
            ) : null}
          </div>
        ) : isParticipant ? (
          <button onClick={handleLeave} disabled={actionLoading} className="btn-secondary w-full" data-testid="leave-session">
            {actionLoading ? 'Leaving...' : 'Leave Session'}
          </button>
        ) : myRequestStatus === 'pending' ? (
          <button disabled className="btn-secondary w-full opacity-80" data-testid="request-pending-state">
            <Clock size={18} className="inline mr-1.5" weight="duotone" />
            Request Pending Host Approval
          </button>
        ) : myRequestStatus === 'rejected' ? (
          <button disabled className="btn-secondary w-full opacity-60" data-testid="request-rejected-state">
            <X size={18} className="inline mr-1.5" weight="bold" />
            Host Declined Your Request
          </button>
        ) : (
          <button
            onClick={handleJoin}
            disabled={actionLoading || isFull}
            className="btn-primary w-full"
            data-testid="join-session"
          >
            {actionLoading ? 'Loading...' : isFull ? 'Session Full' : (session.requires_approval && !session.shop_id ? (
              <span className="flex items-center justify-center gap-2"><Lock size={16} weight="duotone" /> Request to Join</span>
            ) : 'Join Session')}
          </button>
        )}
      </div>

      {/* ============ JOIN REQUEST MODAL ============ */}
      <AnimatePresence>
        {showJoinRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
            onClick={e => e.target === e.currentTarget && setShowJoinRequest(false)}
            data-testid="join-request-modal"
          >
            <motion.div
              initial={{ y: 100, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 100, scale: 0.95 }}
              className="bg-[#0e0e16] border border-white/[0.06] rounded-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 mb-1">
                  <Lock size={18} weight="duotone" className="text-[#9D4CDD]" />
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Request to Join</h3>
                </div>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">
                  This host keeps the exact address private. Send a quick intro and they'll approve or decline. You'll get a notification with the result.
                </p>
              </div>

              <div className="p-5 space-y-3">
                <label className="block text-xs font-medium text-[#A1A1AA]">
                  <EnvelopeSimple size={12} className="inline mr-1" />
                  Message to host (optional)
                </label>
                <textarea
                  value={joinRequestMessage}
                  onChange={e => setJoinRequestMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Hey! I'd love to join. I've played D&D for a few years and I'm easygoing about character builds…"
                  className="input-field w-full text-sm resize-none"
                  data-testid="join-request-message-input"
                />
                <p className="text-[10px] text-[#71717A] text-right">{joinRequestMessage.length}/500</p>
              </div>

              <div className="p-5 pt-2 flex gap-2">
                <button
                  onClick={() => { setShowJoinRequest(false); setJoinRequestMessage(''); }}
                  className="btn-secondary flex-1"
                  data-testid="join-request-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submitJoinRequest}
                  disabled={actionLoading}
                  className="btn-primary flex-1"
                  data-testid="join-request-submit"
                >
                  {actionLoading ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ============ SHARE / INVITE MODAL ============ */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
            onClick={e => e.target === e.currentTarget && setShowShareModal(false)}
            data-testid="share-modal"
          >
            <motion.div
              initial={{ y: 100, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 100, scale: 0.95 }}
              className="bg-[#0e0e16] border border-white/[0.06] rounded-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShareNetwork size={18} weight="duotone" className="text-[#00F0FF]" />
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Share Event</h3>
                </div>
                <button onClick={() => setShowShareModal(false)} className="text-[#666] hover:text-white" data-testid="share-modal-close">
                  <X size={20} />
                </button>
              </div>

              {/* Tabs (only host can invite friends) */}
              {isHost && (
                <div className="flex border-b border-white/[0.06]">
                  <button
                    onClick={() => setShareTab('invite')}
                    className={`flex-1 py-3 text-sm font-medium transition-all ${shareTab === 'invite' ? 'text-white border-b-2 border-[#9D4CDD]' : 'text-[#71717A]'}`}
                    data-testid="share-tab-invite"
                  >
                    <PaperPlaneTilt size={14} className="inline mr-1.5" weight="duotone" />
                    Invite Friends
                  </button>
                  <button
                    onClick={() => setShareTab('link')}
                    className={`flex-1 py-3 text-sm font-medium transition-all ${shareTab === 'link' ? 'text-white border-b-2 border-[#00F0FF]' : 'text-[#71717A]'}`}
                    data-testid="share-tab-link"
                  >
                    <LinkIcon size={14} className="inline mr-1.5" weight="duotone" />
                    Share Link
                  </button>
                </div>
              )}

              {/* INVITE FRIENDS TAB */}
              {shareTab === 'invite' && isHost && (
                <div className="p-5 space-y-3">
                  <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                    <input
                      value={friendSearch}
                      onChange={e => setFriendSearch(e.target.value)}
                      placeholder="Search your friends..."
                      className="w-full bg-[#0a0a12] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40"
                      data-testid="friend-search-input"
                    />
                  </div>

                  <div className="max-h-[40vh] overflow-y-auto space-y-1" data-testid="friend-list">
                    {friendsLoading && (
                      <div className="text-center py-4">
                        <div className="inline-block w-5 h-5 border-2 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {!friendsLoading && filteredFriends.length === 0 && (
                      <div className="text-center py-6">
                        <p className="text-[#71717A] text-sm">
                          {friends.length === 0 ? "You don't have any friends to invite yet" : 'No friends match'}
                        </p>
                        {friends.length === 0 && (
                          <button
                            onClick={() => { setShowShareModal(false); navigate('/friends'); }}
                            className="text-[#9D4CDD] text-xs mt-2 hover:underline"
                          >
                            Find friends →
                          </button>
                        )}
                      </div>
                    )}
                    {filteredFriends.map(f => {
                      const selected = selectedFriendIds.includes(f.id);
                      return (
                        <button
                          key={f.id}
                          onClick={() => toggleFriendSelection(f.id)}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${selected ? 'bg-[#9D4CDD]/15 border border-[#9D4CDD]/40' : 'hover:bg-white/[0.04] border border-transparent'}`}
                          data-testid={`friend-item-${f.id}`}
                        >
                          {f.avatar ? (
                            <img src={f.avatar} alt={f.name} className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-xs font-bold text-white">
                              {f.name?.charAt(0)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{f.name}</p>
                            {f.username && <p className="text-[#71717A] text-[10px]">@{f.username}</p>}
                          </div>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${selected ? 'bg-[#9D4CDD] border-[#9D4CDD]' : 'border-white/20'}`}>
                            {selected && <Check size={12} weight="bold" className="text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={sendInvites}
                    disabled={inviting || selectedFriendIds.length === 0}
                    className="btn-primary w-full"
                    data-testid="send-invites-btn"
                  >
                    {inviting ? 'Sending…' : selectedFriendIds.length > 0
                      ? `Send ${selectedFriendIds.length} Invite${selectedFriendIds.length === 1 ? '' : 's'}`
                      : 'Pick friends to invite'}
                  </button>
                </div>
              )}

              {/* SHARE LINK TAB (default for non-hosts) */}
              {(shareTab === 'link' || !isHost) && (
                <div className="p-5 space-y-3">
                  <p className="text-[#A1A1AA] text-xs">
                    Share this link via text, email, or any chat app. Anyone with the link can view the event page.
                    {session.requires_approval && ' Joining still requires your approval.'}
                  </p>
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a12] border border-white/[0.06]">
                    <LinkIcon size={14} className="text-[#71717A] shrink-0" />
                    <input
                      readOnly
                      value={shareUrl}
                      onClick={(e) => e.target.select()}
                      className="flex-1 bg-transparent text-white text-xs outline-none truncate"
                      data-testid="share-url-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={copyShareLink} className="btn-secondary flex items-center justify-center gap-2" data-testid="copy-link-btn">
                      <Copy size={16} weight="duotone" /> Copy
                    </button>
                    <button onClick={nativeShare} className="btn-primary flex items-center justify-center gap-2" data-testid="native-share-btn">
                      <ShareNetwork size={16} weight="duotone" /> Share
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-1">
                    <a
                      href={`sms:?&body=${encodeURIComponent(`Join my ${session.game_type || 'game'} session on CyBoard: ${shareUrl}`)}`}
                      className="text-center py-2.5 rounded-lg bg-[#0a0a12] border border-white/[0.06] text-[#A1A1AA] text-xs hover:border-[#39FF14]/30 hover:text-[#39FF14]"
                      data-testid="share-sms"
                    >
                      Text
                    </a>
                    <a
                      href={`mailto:?subject=${encodeURIComponent(`Join my ${session.game_type || 'game'} session`)}&body=${encodeURIComponent(`Hey! I'm hosting a session — come join: ${shareUrl}`)}`}
                      className="text-center py-2.5 rounded-lg bg-[#0a0a12] border border-white/[0.06] text-[#A1A1AA] text-xs hover:border-[#00F0FF]/30 hover:text-[#00F0FF]"
                      data-testid="share-email"
                    >
                      Email
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Join my ${session.game_type || 'game'} session on CyBoard: ${shareUrl}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-center py-2.5 rounded-lg bg-[#0a0a12] border border-white/[0.06] text-[#A1A1AA] text-xs hover:border-[#25D366]/30 hover:text-[#25D366]"
                      data-testid="share-whatsapp"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Report Event Modal */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        entityType="session"
        entityId={id}
        entityLabel={session?.title || 'this event'}
      />
    </div>
  );
}
